import React, { useContext } from "react";
import IllustrationOne from "../../../../assets/images/cloudPlus/IllustrationOne.svg";
import IllustrationTwo from "../../../../assets/images/cloudPlus/illustrationTwo.svg";
import IllustrationThree from "../../../../assets/images/cloudPlus/illustrationThree.svg";
import IllustrationFour from "../../../../assets/images/cloudPlus/illustrationFour.svg";
import CatalogueBackground from "../../../../assets/images/download-cutsheet2.png";
import useSWR from "swr";
import DownloadSection from "../../../../lib/downloadSection";
import { fetcher } from "../../../../lib/apiConstants";
import { LoginContext } from "../../../../lib/loginContext";
import { CloudPlusOverviewSection } from "./CloudPlusOverviewSection";

const PAGE_X = "px-6 lg:px-[120px]";

const FeatureSection = ({
  image,
  label,
  description,
  imageLeft = true,
  sectionBg = "#f8f8f8",
  textAlign = "left",
  bodyColor = "#3b3b3b",
  imageMaxWidth = "590px",
}) => (
  <div className="bg-white w-full flex flex-col lg:flex-row lg:h-[540px]">
    <div
      className={`w-full lg:w-1/2 flex items-center justify-center py-5 lg:py-0 min-h-[320px] lg:min-h-0 order-1 ${imageLeft ? "lg:order-1" : "lg:order-2"
        }`}
      style={{ backgroundColor: sectionBg }}
    >
      <img
        src={image}
        alt={label}
        className="h-auto object-contain px-6 lg:px-[65px]"
        style={{ maxWidth: imageMaxWidth, width: "100%" }}
      />
    </div>
    <div
      className={`w-full lg:w-1/2 flex items-center justify-center min-h-[320px] lg:min-h-0 px-6 lg:px-[65px] order-2 ${imageLeft ? "lg:order-2" : "lg:order-1"
        }`}
      style={{ backgroundColor: sectionBg }}
    >
      <div
        className={`w-full max-w-[590px] flex flex-col gap-5 ${textAlign === "right"
          ? "text-right items-end"
          : "text-left items-start"
          }`}
      >
        <h3 className="text-[#171717] text-2xl font-medium font-['Inter'] leading-normal w-full">
          {label}
        </h3>
        <div
          className="text-base font-normal font-['Poppins'] leading-[26px] w-full"
          style={{ color: bodyColor }}
        >
          {description}
        </div>
      </div>
    </div>
  </div>
);

export const CloudPlusContent = () => {
  const { data: products, isLoading: productsLoading } = useSWR(
    "products/",
    fetcher
  );
  const { isLoggedIn } = useContext(LoginContext);

  const features = [
    {
      key: "remote-management-connector",
      image: IllustrationOne,
      label: "Remote Management Connector",
      imageLeft: true,
      sectionBg: "#f8f8f8",
      textAlign: "left",
      bodyColor: "#3b3b3b",
      imageMaxWidth: "720px",
      description: (
        <div className="space-y-4">
          <p>
            Dealers need access to simplify set up, on going configuration
            support and deliver agreed upon SLA and system services. End users
            want to co manage the system with the flexibility of not having to
            be in the facility or with specific computers.
          </p>
          <p>
            The economic engine of our industry is driven by month charged
            services. The goal of RMC is to deliver a suite of services that
            maximizes the (ROI) on the client's initial investment while
            providing capabilities to lower indirect costs of systems use (Low
            TCO).
          </p>
          <p>
            Today's contracts values are sold at a 35X. Every sales should have
            a service contract. RMC offer a suite of services that provide
            tangible enhancements to each technology deployment.
          </p>
        </div>
      ),
    },
    {
      key: "full-control",
      image: IllustrationTwo,
      label: "Full Control, Anywhere, Anytime",
      imageLeft: false,
      sectionBg: "#ffffff",
      textAlign: "right",
      bodyColor: "#5b5b5b",
      imageMaxWidth: "720px",
      description: (
        <>
          <p className="mb-0">
            The key to this design is the systems administrator&apos;s ability
            to have 100% control of the platform locally as well as through the
            cloud.
          </p>
          <p>
            If the cloud is not accessible, the administrator has local control
            to manage and make changes. Both approaches provide seamless
            management of single or multiple sites as a unified platform.
          </p>
        </>
      ),
    },
    {
      key: "secure-connectivity-wan",
      image: IllustrationThree,
      label: "Secure Connectivity and Integration",
      imageLeft: true,
      sectionBg: "#f8f8f8",
      textAlign: "left",
      bodyColor: "#3b3b3b",
      imageMaxWidth: "720px",
      description:
        "RMC Plus leverages the WAN to connect all primary hardware. RMC Plus provides secure remote system management, remote updates, secure backups, and seamless third-party integrations.",
    },
    {
      key: "secure-connectivity-rmc",
      image: IllustrationFour,
      label: "Secure Connectivity and Integration",
      imageLeft: false,
      sectionBg: "#ffffff",
      textAlign: "left",
      bodyColor: "#5b5b5b",
      imageMaxWidth: "720px",
      description:
        "RMC Plus leverages the WAN to connect all primary hardware. RMC Plus provides secure remote system management, remote updates, secure backups, and seamless third-party integrations.",
    },
  ];

  return (
    <>
      <section
        className={`bg-white flex flex-col items-center justify-center gap-6 ${PAGE_X} pt-28 sm:pt-24 min-h-[258px] text-center`}
      >
        <h1 className="text-black text-[32px] font-medium font-['Inter'] leading-normal max-w-[1004px]">
          Hybrid Cloud Management with RMC Plus
        </h1>
        <p className="text-[#5b5b5b] text-[20px] font-medium font-['Poppins'] leading-normal max-w-[872px]">
          Combine the reliability of local system control with the flexibility
          of secure cloud connectivity for seamless multi-site access management
          from anywhere.
        </p>
      </section>

      <div className="w-full bg-white mt-6">
        {features.map((feature) => (
          <FeatureSection
            key={feature.key}
            image={feature.image}
            label={feature.label}
            description={feature.description}
            imageLeft={feature.imageLeft}
            sectionBg={feature.sectionBg}
            textAlign={feature.textAlign}
            bodyColor={feature.bodyColor}
            imageMaxWidth={feature.imageMaxWidth}
          />
        ))}
      </div>

      <section className={`bg-[#f8f8f8] ${PAGE_X} py-[60px]`}>
        <div className="max-w-[1202px] mx-auto flex flex-col items-center gap-4 text-center">
          <h2 className="text-[#5b5b5b] text-[32px] font-medium font-['Inter'] leading-normal w-full">
            Our core focus
          </h2>
          <p className="text-[#5b5b5b] text-base font-normal font-['Poppins'] leading-[26px] w-full">
            Simplicity, Ease of use, and the Convergence of multiple
            technologies within a Single, Unified Platform
          </p>
        </div>
      </section>

      <CloudPlusOverviewSection />
      {isLoggedIn && (
        <div className={`  mx-auto pb-0 `}>
          <DownloadSection
            title="Cut Sheet"
            description="Learn about 2.0 RMC Plus features"
            backgroundImage={CatalogueBackground}
            buttonText="Download Now"
            linkField="others_link"
            products={products || []}
            isLoading={productsLoading}
            filterBySlug="2-dmpl"
            showOnlyFirst={true}
          />
        </div>
      )}
    </>
  );
};
